<template>
  <main class="token-detail-page">
    <AppNavBar :title="t('wallet.tokenDetail')" />

    <section class="token-card">
      <div class="token-card__head">
        <div v-if="coinImage" class="token-card__icon">
          <app-image :src="coinImage" alt="" />
        </div>
        <div v-else class="token-card__icon token-card__icon--fallback">
          {{ coinSymbol.slice(0, 1).toUpperCase() }}
        </div>
        <div class="token-card__meta">
          <div data-strong>{{ coinSymbol }}</div>
          <!-- <span>{{ shortAddress(contractAddress) || '--' }}</span> -->
        </div>
      </div>

      <div class="token-balance">
        <span>{{ t('wallet.propertyBalance') }}</span>
        <div>
          <div data-strong>{{ formatTokenAmount(balance, 8) }}</div>
          <small>${{ totalValue }}</small>
        </div>
      </div>
    </section>

    <section class="record-tabs">
      <button
        v-for="item in tabItems"
        :key="item.key"
        type="button"
        :class="{ active: currentFilter === item.key }"
        @click="changeFilter(item.key)"
      >
        {{ item.label }}
      </button>
    </section>

    <van-pull-refresh v-model="refreshing" @refresh="refreshList">
      <van-list
        :loading="loading"
        :finished="finished"
        :immediate-check="false"
        :finished-text="groupedEntries.length ? t('common.noMore') : ''"
        @load="loadList"
      >
        <section v-if="!groupedEntries.length && !loading" class="empty-state">
          <app-image src="@/assets/images/emptydata_gray.png" alt="" />
          <span>{{ t('wallet.noTransferRecord') }}</span>
        </section>

        <section v-for="[dateKey, items] in groupedEntries" :key="dateKey" class="record-group">
          <h3>{{ dateKey }}</h3>
          <button
            v-for="item in items"
            :key="item.hash || item.time || item.createTime"
            type="button"
            class="record-row"
            @click="openTrade(item)"
          >
            <div class="record-row__icon">
              <van-icon :name="recordDirection(item) === 'in' ? 'down' : 'up'" />
            </div>
            <div class="record-row__content">
              <div class="record-row__title">
                <div data-strong>{{ recordTitle(item) }}</div>
                <div data-strong>{{ recordAmount(item) }}</div>
              </div>
              <div class="record-row__meta">
                <span class="record-row__status" :class="`is-${recordStatus(item)}`">
                  <van-icon :name="recordStatusIcon(item)" />
                  {{ `${recordStatusText(item)} · ${recordTime(item)}` }}
                </span>
                <span>${{ recordValue(item) }}</span>
              </div>
            </div>
          </button>
        </section>
      </van-list>
    </van-pull-refresh>
  </main>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useProfileStore } from '@/stores/profile';
import { useWalletBillStore } from '@/stores/wallet-bill';
import { useWalletTokenStore } from '@/stores/wallet-token';
import { useWalletTokenDetailStore } from '@/stores/wallet-token-detail';
import { getRouteState, navigateWithState } from '@/utils/route-state';
import { formatMoney, formatTokenAmount, shortAddress } from '@/utils/format';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const profileStore = useProfileStore();
const walletBillStore = useWalletBillStore();
const walletTokenStore = useWalletTokenStore();
const walletTokenDetailStore = useWalletTokenDetailStore();
const pageState = computed(() => getRouteState(route, 'wallet-token-detail', {}));

const refreshing = ref(false);

const routeAssetKey = computed(() => ({
  symbol: String(pageState.value.symbol || '').trim(),
  coin: String(pageState.value.symbol || '').trim(),
  contractAddress: String(pageState.value.contractAddress || '').trim()
}));
const currentAsset = computed(() => (
  walletTokenStore.findAssetByKey(routeAssetKey.value) || walletTokenStore.currentAsset || null
));
const coinSymbol = computed(() => String(currentAsset.value?.symbol || currentAsset.value?.coin || pageState.value.symbol || 'Token'));
const coinImage = computed(() => String(currentAsset.value?.img || currentAsset.value?.logo || '').trim());
const contractAddress = computed(() => String(currentAsset.value?.contractAddress || pageState.value.contractAddress || '').trim());
const balance = computed(() => Number(currentAsset.value?.balance || 0));
const balanceTo = computed(() => Number(currentAsset.value?.balanceTo || 0));
const usdtPrice = computed(() => Number(currentAsset.value?.usdtPrice || currentAsset.value?.price || 0));
const totalValue = computed(() => {
  if (balanceTo.value > 0) {
    return formatMoney(balanceTo.value);
  }
  return formatMoney(balance.value * usdtPrice.value);
});
const list = computed(() => walletTokenDetailStore.list);
const loading = computed(() => walletTokenDetailStore.loading);
const finished = computed(() => walletTokenDetailStore.finished);
const currentFilter = computed(() => walletTokenDetailStore.filter);

const tabItems = computed(() => [
  { key: 'all', label: t('wallet.all') },
  { key: 'in', label: t('wallet.transferIn') },
  { key: 'out', label: t('wallet.transferOut') }
]);

const groupedEntries = computed(() => {
  const groups = new Map();
  list.value.forEach((item) => {
    const key = recordDate(item);
    const prev = groups.get(key) || [];
    prev.push(item);
    groups.set(key, prev);
  });
  return Array.from(groups.entries());
});

function recordDirection(item) {
  const type = String(item?.type || '').toLowerCase();
  if (type === 'in' || type === 'out') {
    return type;
  }
  const currentAddress = String(profileStore.currentAddress || '').toLowerCase();
  const toAddress = String(item?.toAddress || '').toLowerCase();
  return currentAddress && toAddress && currentAddress === toAddress ? 'in' : 'out';
}

function recordDate(item) {
  const date = parseRecordDate(item);
  if (!date) {
    return '--';
  }
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function recordTime(item) {
  const date = parseRecordDate(item);
  if (!date) {
    return '--';
  }
  const pad = (value) => String(value).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function parseRecordDate(item) {
  if (item?.time) {
    const parsed = new Date(item.time);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  const raw = Number(item?.createTime || 0);
  if (!raw) {
    return null;
  }
  return new Date(raw < 10000000000 ? raw * 1000 : raw);
}

function recordTitle(item) {
  const direction = recordDirection(item) === 'in' ? t('wallet.transferFrom') : t('wallet.transferTo');
  const addressText = recordDirection(item) === 'in'
    ? shortAddress(item?.fromAddress) || '--'
    : shortAddress(item?.toAddress) || '--';
  return `${direction} ${addressText}`;
}

function recordAmount(item) {
  const amount = Number(item?.amount || 0);
  const prefix = recordDirection(item) === 'in' ? '+' : '-';
  return `${prefix} ${formatTokenAmount(amount, 8)}`;
}

function recordValue(item) {
  const amount = Number(item?.amount || 0);
  return formatMoney(amount * usdtPrice.value);
}

function recordStatus(item) {
  const status = Number(item?.status);
  if (status === 1) {
    return 'success';
  }
  if (status === 0) {
    return 'pending';
  }
  return 'failed';
}

function recordStatusText(item) {
  return t(`wallet.${recordStatus(item)}`);
}

function recordStatusIcon(item) {
  const status = recordStatus(item);
  if (status === 'success') {
    return 'passed';
  }
  if (status === 'pending') {
    return 'clock-o';
  }
  return 'close';
}

async function loadList(reset = false) {
  await walletTokenDetailStore.fetchNext({
      chainNum: profileStore.currentNetwork.chainNum || 56,
      address: profileStore.currentAddress || '',
      contractAddress: contractAddress.value || null,
      reset
    }, { meta: { showLoading: !reset } });
}

async function refreshList() {
  await loadList(true);
  refreshing.value = false;
}

async function changeFilter(filterKey) {
  if (currentFilter.value === filterKey) {
    return;
  }
  walletTokenDetailStore.setFilter(filterKey);
  await loadList(true);
}

function openTrade(item) {
  walletBillStore.setCurrentTrade({
    ...item,
    symbol: coinSymbol.value,
    usdtPrice: usdtPrice.value,
    img: coinImage.value
  });
  navigateWithState(router, '/wallet/bill/detail', {
    namespace: 'wallet-bill-detail',
    payload: {
      symbol: coinSymbol.value,
      coin: coinSymbol.value,
      logLevelMsg: recordTitle(item),
      addOrSubAmount: String(recordDirection(item) === 'in' ? Number(item?.amount || 0) : -Number(item?.amount || 0)),
      createTime: String(item?.createTime || ''),
      time: String(item?.time || ''),
      amount: String(item?.amount || '0'),
      status: String(item?.status ?? ''),
      type: String(recordDirection(item)),
      fromAddress: String(item?.fromAddress || ''),
      toAddress: String(item?.toAddress || ''),
      hash: String(item?.hash || ''),
      chainName: String(item?.chainName || ''),
      chainImg: String(item?.chainImg || ''),
      explorerUrl: String(item?.explorerUrl || ''),
      usdtPrice: String(usdtPrice.value || 0),
      img: String(coinImage.value || '')
    }
  });
}

async function ensureAssetReady() {
  const matched = await walletTokenStore.ensureCurrentAsset({
    address: profileStore.currentAddress || '',
    ...routeAssetKey.value
  }, { meta: { showLoading: false } });

  if (!matched) {
    walletTokenStore.syncCurrentAsset(routeAssetKey.value);
  }
}

function syncDetailScope() {
  walletTokenDetailStore.syncAssetKey({
    symbol: routeAssetKey.value.symbol,
    contractAddress: routeAssetKey.value.contractAddress
  });
}

watch(
  () => [profileStore.currentAddress, pageState.value.symbol, pageState.value.contractAddress],
  async () => {
    syncDetailScope();
    await ensureAssetReady();
    await loadList(true);
  },
  { immediate: false }
);

onMounted(async () => {
  syncDetailScope();
  await ensureAssetReady();
  await loadList(true);
});
</script>

<style scoped lang="scss">
.token-detail-page {
  min-height: var(--app-viewport-height);
  padding: 0 14px 24px;
  background: #f7f7f7;
}

.token-card,
.record-group,
.record-tabs {
  border-radius: 12px;
  background: #ffffff;
}

.token-card {
  margin-top: 14px;
  padding: 16px;
}

.token-card__head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.token-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 25px;
  overflow: hidden;

  .app-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.token-card__icon--fallback {
  color: #ffffff;
  font-size: 20px;
  font-weight: 700;
}

.token-card__meta {
  min-width: 0;

  [data-strong],
  span {
    display: block;
  }

  [data-strong] {
    color: #111111;
    font-size: 18px;
    font-weight: 500;
  }

  span {
    margin-top: 4px;
    color: #999999;
    font-size: 12px;
  }
}

.token-balance {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 18px;

  > span {
    color: #111111;
    font-size: 16px;
    font-weight: 500;
  }

  div {
    text-align: right;
  }

  [data-strong],
  small {
    display: block;
  }

  [data-strong] {
    color: #111111;
    font-size: 18px;
    font-weight: 500;
  }

  small {
    margin-top: 4px;
    color: #666666;
    font-size: 14px;
  }
}

.record-tabs {
  display: flex;
  gap: 20px;
  margin: 10px 0;
  padding: 0 16px;

  button {
    padding: 18px 0 14px;
    border: 0;
    background: transparent;
    color: #666666;
    font-size: 15px;
  }

  button.active {
    color: #111111;
    font-weight: 500;
    border-bottom: 2px solid var(--wp-color-theme);
  }
}

.record-group {
  margin-top: 10px;
  padding: 14px 15px;

  h3 {
    margin: 0 0 10px;
    color: #666666;
    font-size: 14px;
    font-weight: 500;
  }
}

.record-row {
  display: flex;
  width: 100%;
  padding: 12px 0;
  border: 0;
  border-bottom: 1px solid #f2f3f5;
  background: transparent;
  text-align: left;

  &:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }
}

.record-row__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-right: 10px;
  border-radius: 16px;
  background: #edf3ff;
  color: var(--wp-color-theme);
  flex: 0 0 32px;
}

.record-row__content {
  flex: 1;
  min-width: 0;
}

.record-row__title,
.record-row__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.record-row__title {
  [data-strong] {
    color: #111111;
    font-size: 14px;
    font-weight: 500;

    &:last-child {
      flex: 0 0 auto;
    }
  }
}

.record-row__meta {
  margin-top: 6px;

  span {
    color: #999999;
    font-size: 12px;
  }
}

.record-row__status {
  display: inline-flex;
  align-items: center;
  gap: 4px;

  .van-icon {
    font-size: 13px;
  }

  &.is-success {
    color: #07b077;
  }

  &.is-pending {
    color: #9aa0a6;
  }

  &.is-failed {
    color: #ef0027;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 120px;

  .app-image {
    width: 100px;
    height: 100px;
    object-fit: contain;
  }

  span {
    color: #999999;
    font-size: 14px;
  }
}
</style>
